// Intake form constants
export const ACCIDENT_TYPES = [
  'Motor Vehicle Accident',
  'Medical Malpractice',
  'Miscellaneous',
  'Breach of Employment Contract',
  'Seller Non-Disclosure',
  'Premises Liability',
  'Broker Negligence',
  'Dog Bite',
  'Wrongful Death',
  'Property Damage',
  'Mold',
  'Unknown',
] as const;

export const CONTACT_METHODS = ['Phone', 'Text'] as const;

export const HEALTH_TYPES = [
  'Private',
  'Medicare',
  'Medicaid',
  'VA',
  'Other',
] as const;

export type SectionKey =
  | 'personalInfo'
  | 'incidentInfo'
  | 'insurance'
  | 'medicalWageHistory'
  | 'legalLifestyle';

export const SECTION_LABELS: Record<SectionKey, string> = {
  personalInfo: 'Personal & Emergency Info',
  incidentInfo: 'Incident & Liability',
  medicalWageHistory: 'Medical & Injuries',
  insurance: 'Insurance Information',
  legalLifestyle: 'Legal & Life Impact',
};

export const STATUS_VARIANTS = {
  'In Progress': 'default',
  'Converted to Matter': 'success',
  Rejected: 'destructive',
} as const;

export const INTAKE_STATUS_OPTIONS = [
  'In Progress',
  'Converted to Matter',
  'Rejected',
] as const;

export type IntakeStatus = (typeof INTAKE_STATUS_OPTIONS)[number];

export const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const;

// State name to abbreviation mapping
export const STATE_ABBREV: Record<string, string> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

// Abbreviation to state name mapping (reverse lookup)
export const ABBREV_TO_STATE: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREV).map(([state, abbrev]) => [abbrev, state]),
);

// Rejection reason options (shown when intake status is 'Rejected')
// Grouped by category for logical organization
export const REJECTION_REASONS = [
  // Liability & Coverage Issues
  'Liability Issue',
  'At Fault',
  'No Insurance Coverage',
  'Government Immunity Issues',
  'Work Comp Exclusive',
  // Injury & Treatment Issues
  'No Viable Injury',
  'No Treatment',
  'Treatment Gap',
  'Damages Too Low',
  // Procedural/Legal Issues
  'Past SOL/SOL Concern',
  'Conflict of Interest',
  'Outside Practice Area',
  'Out of State/Service Area',
  'Already Has Counsel',
  // Contact/Communication Issues
  'Unresponsive',
  'Never Made Initial Contact',
  'Adult Calling for Another Adult/No POA',
  // Administrative
  'Duplicate',
  'Spam',
  'Referred Out',
  'Other', // When selected, shows free text field for custom reason
] as const;

export type RejectionReason = (typeof REJECTION_REASONS)[number];

// Referral source options
export const REFERRAL_SOURCES = [
  { value: 'Walker', label: 'Walker', group: 'Direct' },
  { value: 'Top Dog Law', label: 'Top Dog Law', group: 'Direct' },
  { value: 'Suite 1000', label: 'Suite 1000', group: 'Direct' },
  { value: 'Atrium', label: 'Atrium', group: 'Direct' },
  { value: '1-800 Lawyers', label: '1-800 Lawyers', group: 'Direct' },
  { value: 'Attorney Share', label: 'Attorney Share', group: 'Direct' },
  { value: 'Juvo', label: 'Juvo', group: 'Direct' },
  { value: 'Case XChange', label: 'Case XChange', group: 'Direct' },
  { value: 'Internet', label: 'Internet', group: 'Direct' },
  { value: 'No Fault Group SLG TX', label: 'No Fault Group SLG TX', group: 'Direct' },
  { value: 'No Fault Group 800', label: 'No Fault Group 800', group: 'Direct' },
  { value: 'Other', label: 'Other', group: 'Direct' },
  {
    value: 'Referral Firm - Morgan & Morgan',
    label: 'Morgan & Morgan',
    group: 'Referral Firm',
  },
  {
    value: 'Referral Firm - Brandon J. Broderick',
    label: 'Brandon J. Broderick',
    group: 'Referral Firm',
  },
  { value: 'Referral Firm - Other', label: 'Other', group: 'Referral Firm' },
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCES)[number]['value'];

// Case type mapping from human-readable (UI) to snake_case (API)
// Used when fetching screening configuration from the backend
export const CASE_TYPE_TO_API: Record<string, string> = {
  'Motor Vehicle Accident': 'auto_accident',
  'Medical Malpractice': 'medical_malpractice',
  Miscellaneous: 'miscellaneous',
  'Breach of Employment Contract': 'employment_contract',
  'Seller Non-Disclosure': 'seller_non_disclosure',
  'Premises Liability': 'premise_liability',
  'Broker Negligence': 'broker_negligence',
  'Dog Bite': 'dog_bite',
  'Wrongful Death': 'wrongful_death',
  'Property Damage': 'property_damage',
  Mold: 'mold',
  Unknown: 'unknown',
};

// Reverse mapping: snake_case (API) to human-readable (UI)
export const API_TO_CASE_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(CASE_TYPE_TO_API).map(([ui, api]) => [api, ui]),
);

/**
 * Convert a human-readable case type to the API snake_case format.
 * Returns undefined if no mapping exists.
 */
export function getCaseTypeApiValue(
  humanReadable: string | undefined,
): string | undefined {
  if (!humanReadable) return undefined;
  return CASE_TYPE_TO_API[humanReadable];
}
